/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from "@angular/core";
import { Resolve } from "@angular/router";
import { AreaOfWork } from "@models/areaOfWork.model";
import { CapitalNeed } from "@models/capitalNeed.model";
import { CityBorough } from "@models/city-borough.model";
import { Commitment } from "@models/commitment.model";
import { Development } from "@models/development.model";
import { Funding } from "@models/funding.model";
import { Project } from "@models/project.model";
import { Schedule } from "@models/schedule.model";
import { Worktypes } from "@models/worktypes.model";
import { CapitalNeedsService } from "@services/capital-needs.service";
import { CityBoroughService } from "@services/city-borough.service";
import { CommitmentService } from "@services/commitment.service";
import { DataStoreService } from "@services/data-store.service";
import { DevelopmentService } from "@services/development.service";
import { FundingService } from "@services/funding.service";
import { ImageProcessingService } from "@services/image-processing.service";
import { ProjectService } from "@services/project.service";
import { ScheduleService } from "@services/schedule.service";
import { WorktypeService } from "@services/worktype.service";
import { lastValueFrom, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class BaseResolver implements Resolve<any> {
  // sortingAreaOfWork = [
  //   "Roofs",
  //   "Building Exteriors/Facades/Windows",
  //   "Heating",
  //   "Elevators",
  //   "Waste Management",
  //   "Interior Electrical/Lighting",
  //   "Plumbing",
  //   "Safety And Security",
  //   "Apartments",
  //   "Common Areas/Lobbies",
  //   "Grounds",
  //   "Other",
  // ];

  sortingScheduleByDesignBuild = ["Planning", "Procurement", "Design", "Construction"];

  sortingScheduleByNotDesignBuild = ["Planning", "Design", "Procurement", "Construction"];
  tobeDetermined = "To Be Determined";
  constructor(
    private dataService: DataStoreService,
    private fundingService: FundingService,
    private projectService: ProjectService,
    private scheduleService: ScheduleService,
    private developmentService: DevelopmentService,
    private capitalNeedsService: CapitalNeedsService,
    private imageProcessingService: ImageProcessingService,
    private commitmentService: CommitmentService,
    private workTypeService: WorktypeService,
    private cityBoroughService: CityBoroughService,
  ) { }

  async resolve(): Promise<any> {
    const temp: any = {};
    const cleanProjectList: Project[] = [];
    const cleanScheduleList: Schedule[] = [];
    const cleanfundingList: Funding[] = [];
    const cleanDevelopmentList: Development[] = [];
    const cleanCapitalNeedsList: CapitalNeed[] = [];
    const cleanCommitmentList: Commitment[] = [];
    const cleanWorkTypeList: Worktypes[] = [];
    const cleanCityBoroughList: CityBorough[] = [];
    const isDataLoaded = !this.dataService.isEmpty();

    if (!isDataLoaded) {
      await this.dataService.loadData();

      const projects = this.dataService.getData()["projects"];
      const fundings = this.dataService.getData()["funding"];
      const schedules = this.dataService.getData()["scheduling"];
      const commitments = this.dataService.getData()["commitments"];
      const primaryworktype = this.dataService.getData()["primaryworktype"];
      const cityborough = this.dataService.getData()["borough"];
      // const buildings = this.dataService.getData()["buildings"];
      const developments = this.dataService.getData()["developments"].filter((x: { [x: string]: string }) =>
        x["Include_Exclude"].toLocaleLowerCase() === "Include".toLocaleLowerCase(),
      );

      // const representatives = this.dataService.getData()["representatives"];
      const pnaData = this.dataService.getData()["pna_data"];

      const allowedProjects = projects.filter(
        (project: any) =>
          project["TDS#"] !== undefined ||
          Array.from(developments)
            .flatMap((x: any) => parseInt(x["DevelopmentId"]))
            .includes(parseInt(project["TDS#"])),
      );
      const allowedProjectIds = allowedProjects.map((project: any) => project["Project Id"]);

      if (primaryworktype) {
        for (const data of primaryworktype) {
          cleanWorkTypeList.push({
            ProjectPrimaryWorkTypes: data["Project Primary Work Types"],
            TrackerPrimaryWorkTypes: data["Tracker Primary Work Types"],
            PNAPrimaryWorkTypes: data["PNA Primary Work Types"],
            DisplayOrder: parseInt(data["Display Order"]),
          });
        }
        this.workTypeService.setItems(cleanWorkTypeList);
      }

      if (cityborough) {
        for (const data of cityborough) {
          cleanCityBoroughList.push({
            cityCouncilDistrict: data["City Council District"],
            borough: data["Borough"],
          });
        }
        this.cityBoroughService.setItems(cleanCityBoroughList);
      }

      if (pnaData) {
        for (const data of pnaData) {
          if (
            !Array.from(developments)
              .flatMap((x: any) => parseInt(x["DevelopmentId"] || 0))
              .includes(parseInt(data["development_id"] || 0))
          )
            continue;

          const worktype = this.workTypeService
            .getItems()
            .filter(
              (x) =>
                x.PNAPrimaryWorkTypes.toLocaleUpperCase() ===
                data["primary_worktype"].toLocaleUpperCase(),
            )
            .map((v) => {
              return {
                WorkTypeName: v.TrackerPrimaryWorkTypes,
                displayOrder: v.DisplayOrder,
              };
            });

          cleanCapitalNeedsList.push({
            developmentId: parseInt(data["development_id"]),
            fiveYearCapitalNeeds: parseInt(data["five_year_need"] || 0) ?? 0,
            twentyYearCapitalNeeds: parseInt(data["twenty_year_need"] || 0) ?? 0,
            areaOfWork: worktype.length > 0 ? worktype[0].WorkTypeName : "",
            sortOrder: worktype.length > 0 ? worktype[0].displayOrder : 0,
          });
        }
        cleanCapitalNeedsList.sort((a, b) => a.sortOrder - b.sortOrder);

        this.capitalNeedsService.setItems(cleanCapitalNeedsList.sort((x) => x.sortOrder));
      }

      if (fundings) {
        for (const funding of fundings) {
          if (!allowedProjectIds.includes(funding["Project Id"])) continue;
          cleanfundingList.push(
            new Funding(
              funding["Project Id"],
              funding["Funding Category Description"],
              parseInt(funding["Total Funds"].replaceAll("$", "").replaceAll(",", "") || 0),
              parseInt(funding["Total Budget"].replaceAll("$", "").replaceAll(",", "") || 0),
            ),
          );
        }

        this.fundingService.setItems(cleanfundingList);
      }

      if (schedules) {
        for (const schedule of schedules) {
          if (!allowedProjectIds.includes(schedule["Project Id"])) continue;

          cleanScheduleList.push(
            new Schedule(
              schedule["Project Id"],
              schedule["Master Task Name"],
              Math.round(schedule["Percent Complete"]).toString(),
              new Date(schedule["Baseline Start Date"]).toLocaleDateString(),
              new Date(schedule["Baseline Finish Date"]).toLocaleDateString(),
              new Date(schedule["Start Date"]).toLocaleDateString(),
              new Date(schedule["Finish Date"]).toLocaleDateString(),
              new Date(schedule["Actual Start Date"]).toLocaleDateString(),
              new Date(schedule["Actual Finish Date"]).toLocaleDateString(),
            ),
          );
        }

        this.scheduleService.setItems(cleanScheduleList);
      }

      if (commitments) {
        for (const commitment of commitments) {
          if (!allowedProjectIds.includes(commitment["Project Id"])) continue;

          cleanCommitmentList.push(
            new Commitment(
              commitment["Project Id"],
              commitment["Field Inspector"],
              commitment["Company Name"],
              commitment["Commitment Type"],
            ),
          );
        }

        this.commitmentService.setItems(cleanCommitmentList);
      }

      if (projects) {
        for (const project of allowedProjects) {
          const developmentByTDS: number[] = project["TDS#"]
            .toString()
            .split(",")
            .map((a: string) => parseInt(a));
          const projectName = project["Development"];

          // if (devs.length > 1) {
          //   projectName = project["Development"].split(",")[0];
          // }

          const worktype = this.workTypeService
            .getItems()
            .filter(
              (x) =>
                x.ProjectPrimaryWorkTypes.toLocaleUpperCase() ===
                project["Primary Work Type"].toLocaleUpperCase(),
            )
            .map((v) => {
              return {
                WorkTypeName: v.TrackerPrimaryWorkTypes,
                displayOrder: v.DisplayOrder,
              };
            });

          for (let Id = 0; Id < developmentByTDS.length; Id++) {
            if (allowedProjectIds.includes(developmentByTDS[Id])) continue;
            const developmentRecord: any = Array.from(developments).find(
              (x: any) => parseInt(x["DevelopmentId"]) == developmentByTDS[Id],
            );

            const devName = developmentRecord ? developmentRecord.Name : "";

            cleanProjectList.push(
              new Project(
                project["Project Id"],
                project["Description"],
                project["Project Status"],
                project["Project Scope of Work"],
                developmentByTDS[Id],
                devName,
                project["Project Delivery Type"],
                project["Project Manager"],
                project["Project Update"],
                new Date(Date.UTC(0, 0, project["Updated Date"])),
                // project["Updated Date"],
                project["Department"],
                worktype.length > 0 ? worktype[0].WorkTypeName : "",
                cleanfundingList.filter((item: Funding) => item.projectId == project["Project Id"]),
                await this.getSchedule(project["Project Id"], project["Project Delivery Type"]),
                developmentByTDS,//.filter((d) => d != developmentByTDS[0]),
                project["Project Id"],
                project["Field Inspector"],
                project["Building Numbers"],
                project["Start Year"] ?? "",
                cleanCommitmentList.filter(
                  (item: Commitment) => item.projectId == project["Project Id"],
                ),
                project["Project Location"],
                parseInt(
                  project["Total Budget"].toString().replaceAll("$", "").replaceAll(",", "") || 0,
                ),
              ),
            );
          }
        }

        this.projectService.setItems(cleanProjectList);
      }

      if (developments) {
        const areasOfWork = [...new Set(cleanCapitalNeedsList.map((c) => c.areaOfWork))];

        for (const development of developments) {
          if (
            !Array.from(developments)
              .flatMap((x: any) => parseInt(x["DevelopmentId"]))
              .includes(parseInt(development["DevelopmentId"]))
          )
            continue;

          const projects = cleanProjectList.filter(
            (project: Project) => project.developmentId == parseInt(development["DevelopmentId"]),
          );

          cleanDevelopmentList.push(
            new Development(
              parseInt(development["DevelopmentId"]),
              development["Name"],
              development["Address"],
              development["Borough"],
              development["City_council_district"],
              "",
              "",
              areasOfWork.map((areaOfWork) => {
                return new AreaOfWork(
                  areaOfWork,
                  projects.filter((p) => p.areaOfWork == areaOfWork),
                  cleanCapitalNeedsList
                    .filter(
                      (c) =>
                        c.developmentId == parseInt(development["DevelopmentId"]) &&
                        areaOfWork == c.areaOfWork,
                    )
                    .map((p) => p.fiveYearCapitalNeeds)
                    .reduce((acc, currentValue) => acc + currentValue, 0) ?? 0,
                  cleanCapitalNeedsList
                    .filter(
                      (c) =>
                        c.developmentId == parseInt(development["DevelopmentId"]) &&
                        areaOfWork == c.areaOfWork,
                    )
                    .map((p) => p.twentyYearCapitalNeeds)
                    .reduce((acc, currentValue) => acc + currentValue, 0) ?? 0,
                );
              }),
              0,
              "",
              development["Units"],
              development["Include_Exclude"] ?? "Include",
            ),
          );
        }

        this.developmentService.setItems(cleanDevelopmentList);
      }

      temp["fundings"] = fundings;
      temp["projects"] = projects;
      temp["schedules"] = schedules;
      temp["commitments"] = commitments;
      temp["developments"] = cleanDevelopmentList;
      temp["pnaData"] = cleanCapitalNeedsList;
    }

    // Adds a small delay because the data loads too fast
    return lastValueFrom(of(temp));
  }

  private getSchedule(projectId: string, projectDeliveryType: string): Promise<Schedule[]> {
    const listOfSchedule: Schedule[] = [];
    const _schedules = this.scheduleService.getItems().filter((x) => x.projectId === projectId);
    this.sortingScheduleByDesignBuild.forEach((masterTaskName) => {
      const v = _schedules.find(
        (c) => c.masterTaskName.toLocaleLowerCase() === masterTaskName.toLocaleLowerCase(),
      );
      if (v) {
        listOfSchedule.push(v);
      } else {
        listOfSchedule.push({
          projectId: projectId,
          masterTaskName: masterTaskName,
          percentageCompleted: "0",
          baselineStartDate: this.tobeDetermined,
          baselineFinishDate: this.tobeDetermined,
          startDate: this.tobeDetermined,
          finishDate: this.tobeDetermined,
          actualStartDate: this.tobeDetermined,
          actualFinishDate: this.tobeDetermined,
        });
      }
    });

    if (projectDeliveryType === "Design Build") {
      return lastValueFrom(
        of(
          listOfSchedule.sort(
            (a, b) =>
              this.sortingScheduleByDesignBuild.indexOf(a.masterTaskName) -
              this.sortingScheduleByDesignBuild.indexOf(b.masterTaskName),
          ),
        ),
      );
    } else {
      return lastValueFrom(
        of(
          listOfSchedule.sort(
            (a, b) =>
              this.sortingScheduleByNotDesignBuild.indexOf(a.masterTaskName) -
              this.sortingScheduleByNotDesignBuild.indexOf(b.masterTaskName),
          ),
        ),
      );
    }
  }
}
